import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Plus, Edit, Trash2, X, AlertCircle, Loader, CircleMinus, CirclePlus} from 'lucide-react';
import { useToast } from "../components/ui/toast-provider";

// Servicios
import { UsuarioService } from '../services/usuarioService';

// Tipos de Response
import { UsuarioResponse } from '../types/user';

// Modales
import CrearUsuario from '../modals/CrearUsuario';

import LogoBlanco from '../components/img/LogoBlanco.png';

export function Usuarios() {
  const [usuarios, setUsuarios] = useState<UsuarioResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [usuarioEditando, setUsuarioEditando] = useState<number>(0);
  const [filtroActivo, setFiltroActivo] = useState(true);
  const { addToast } = useToast();

  // Cargar empleados al montar el componente y cada vez que cambie el filtro
  useEffect(() => {
    cargarUsuarios();
  }, [filtroActivo]);

  const cargarUsuarios = async () => {
    try {
      setIsLoading(true);
      const res = await UsuarioService.getAll(filtroActivo);
      setUsuarios(res.data);
    } catch (err) {
      setError("Error al cargar empleados");
    } finally {
      setIsLoading(false);
    }
  };

  const cambiarEstado = async (id: number, activo: boolean) => {
    try {
      setIsLoading(true);
      //console.log("id del usuario: ", id)
      if (activo) {
        await UsuarioService.desactivar(id);
        addToast("Usuario desactivado exitosamente");
      } else {
        await UsuarioService.activar(id);
        addToast("Usuario activado exitosamente");
      }
      await cargarUsuarios();
    } catch (err) {
      setError("Error al cambiar estado del usuario");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-6 md:grid-cols-6 gap-4 p-6 text-white bg-primary border border-primary rounded-t-lg items-center">
        {/* Logo */}
        <div className="col-span-2 md:col-span-1 flex justify-center md:justify-start">
            <img 
            src={LogoBlanco} 
            alt="Logo AGEMI" 
            className="w-20 md:w-40 h-auto object-contain"
            />
        </div>
        <div className="col-span-4 md:col-span-5 flex flex-col text-sm md:text-base">
          <h1 className="text-3xl font-bold">Usuarios AGEMI</h1>
        <p className="text-base text-white mt-1 md:mt-2">
          Gestión de los usuarios del sistema
        </p>
        </div>
      </div>
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
            <Select
              value={filtroActivo ? '1' : '0'}
              onChange={(e) => setFiltroActivo(e.target.value === '1')}
              options={[
                { value: '1', label: 'Usuarios Activos' },
                { value: '0', label: 'Usuarios Inactivos' },
              ]}
              className="w-full md:w-auto bg-white text-primary"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-end mb-4 gap-2">
            {!mostrarFormulario && (
              <Button onClick={() => setMostrarFormulario(true)} variant="accent" className="w-full md:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Usuario
              </Button>
            )}
          </div>

          {error && (
            <div className="p-4 mb-4 bg-destructive/10 border border-destructive rounded-lg text-destructive flex items-start gap-2">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Error al cargar usuarios</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-primary mr-2" />
              <p>Cargando usuarios...</p>
            </div>
          ) : usuarios.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No hay usuarios para mostrar</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[400px] border-gray-400 mb-10">
                <table className="min-w-full text-left">
                  <thead className="bg-primary text-white sticky top-0">
                    <tr>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold">Usuario</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold">Empleado</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold">Rol</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-center font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usuarios.map((usuario) => (
                      <tr key={usuario.idUsuario} className="border-t border-border hover:bg-muted/30 transition-colors">
                        <td className="px-3 md:px-4 py-2 md:py-3 font-medium text-xs md:text-sm">
                          <div className="truncate">{usuario.usuario}</div>
                        </td>
                        <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm">{usuario.empleado}</td>
                        <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm">{usuario.rol}</td>
                        <td className="px-3 md:px-4 py-2 md:py-3">
                          <div className="flex justify-center gap-1 md:gap-2">
                            <Button
                              size="sm"
                              variant="accent"
                              className="text-xs md:text-sm px-2 md:px-3"
                              onClick={() => {
                                setUsuarioEditando(Number(usuario.idUsuario));
                                setMostrarFormulario(true);
                              }}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant={filtroActivo ? "destructive" : "primary"}
                              className="text-xs md:text-sm px-2 md:px-3"
                              onClick={() => {
                                cambiarEstado(Number(usuario.idUsuario), filtroActivo);
                              }}
                            >
                              {filtroActivo ? <CircleMinus size={16} /> : <CirclePlus size={16} />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>
          )}
        </CardContent>
      </Card>

      <CrearUsuario
        isOpen={mostrarFormulario} 
        onClose={async () => {
          setMostrarFormulario(false);
          setUsuarioEditando(0);
        }}
        onGuardado={async() => {
          await cargarUsuarios();
          setMostrarFormulario(false);
        }}
        idEditando = {usuarioEditando !== null ? Number(usuarioEditando) : 0}
      />   
    </div>
  );
}
